import * as React from 'react'

import { useTranslation } from 'react-i18next'

import { Box, Flex, Text } from '@chakra-ui/react'

import {
  Link,
  SideMenuLayoutBreadcrumb,
  SideMenuLayoutContentLayout,
  TertiaryButton,
} from '@enechain/ecloud-designsystem'

import { BillingEmail } from '~/gen/proto/bff/v1/model_pb'
import { useLoginOrganization } from '~/hooks/useLoginOrganization'
import { useLoginUser } from '~/hooks/useLoginUser'
import RowContent from '~/pages/Billing/components/RowContent'
import { useBillingEmails } from '~/pages/Billing/hooks/useBillingEmails'
import { fromTimestamp } from '~/utils/date'

const UpdatedInfo: React.FC<{ billingEmail: BillingEmail }> = ({
  billingEmail,
}) => {
  const { t, i18n } = useTranslation()
  const isEnglish = i18n.language === 'en'
  const updatedAt = billingEmail.updatedAt
    ? fromTimestamp(billingEmail.updatedAt).formatYYYYMMDDHHmm()
    : '-'
  // enechain運用側の更新は個人名を出さず固定文言を表示する
  const updatedBy = billingEmail.isUpdatedByEnechainOperation
    ? t('pages.billing.label.updated_by_enechain_operation')
    : (isEnglish && billingEmail.updatedByEngName
        ? billingEmail.updatedByEngName
        : billingEmail.updatedBy) || '-'
  const updatedByOrg =
    (isEnglish && billingEmail.updatedByOrgEngName
      ? billingEmail.updatedByOrgEngName
      : billingEmail.updatedByOrg) || '-'
  return (
    <Text>
      {t('pages.billing.label.updated_at')}: {updatedAt},{' '}
      {t('pages.billing.label.updated_by')}: {updatedBy}（{updatedByOrg}）
    </Text>
  )
}

export const Billing: React.FC = () => {
  const { t, i18n } = useTranslation()
  const me = useLoginUser()
  const organization = useLoginOrganization()
  const billingEmails = useBillingEmails(me.organizationId)

  const organizationName =
    (i18n.language === 'en' && organization.engName
      ? organization.engName
      : organization.name) || ''

  return (
    <SideMenuLayoutContentLayout
      header={
        <Flex alignItems="center" justifyContent="space-between" width="full">
          <SideMenuLayoutBreadcrumb
            items={[
              {
                label: t('pages.billing.title'),
                bold: true,
              },
            ]}
          />
        </Flex>
      }
    >
      <Flex
        flexDirection="column"
        gap="3"
        maxWidth="1280px"
        margin="auto"
        padding="2"
      >
        <Text textStyle="headerMd" color="Text.highEmphasis" textAlign="left">
          {t('pages.billing.label.subtitle')}
        </Text>
        <Box
          marginBottom="8px"
          padding="4"
          borderRadius="md"
          backgroundColor="Surface.neutralLow"
        >
          <Text color="Text.lowEmphasis" fontWeight="bold">
            {t('pages.billing.description.billing_email_target')}
          </Text>
        </Box>

        <Flex
          flexDirection="column"
          gap="4"
          padding="4"
          borderWidth="1px"
          borderColor="Border.midEmphasis"
          borderRadius="8px"
        >
          {billingEmails.length === 0 ? (
            <Flex alignItems="center" justifyContent="space-between">
              <RowContent
                label={t('pages.billing.label.billing_email')}
                qLabel={t('pages.billing.message.billing_email')}
                width="240px"
                vWidth="400px"
                value={[
                  t('pages.billing.description.billing_email_unregistered'),
                  t('pages.billing.description.billing_email_add_notice'),
                ]}
                valueStyles={{ color: 'Text.midEmphasis' }}
              />
              <Link href="/billing/edit">
                <TertiaryButton>{t('action.edit')}</TertiaryButton>
              </Link>
            </Flex>
          ) : (
            billingEmails.map((billingEmail, index) => (
              <React.Fragment key={billingEmail.productId ?? 0}>
                <Flex alignItems="center" justifyContent="space-between">
                  <Text textStyle="headerMd">
                    {billingEmail.isGeneral
                      ? t('pages.billing.label.general')
                      : billingEmail.productName ||
                        t('pages.billing.label.product')}
                  </Text>
                  {index === 0 && ( // Display at the top of the table
                    <Flex alignItems="center" gap="2">
                      <UpdatedInfo billingEmail={billingEmail} />
                      <Link href="/billing/edit">
                        <TertiaryButton>{t('action.edit')}</TertiaryButton>
                      </Link>
                    </Flex>
                  )}
                </Flex>
                {!billingEmail.isGeneral && (
                  <RowContent
                    label={t('pages.billing.label.display_name')}
                    qLabel={t('pages.billing.message.display_name')}
                    width="240px"
                    vWidth="400px"
                    value={billingEmail.displayName || organizationName}
                    valueStyles={{ color: 'Text.midEmphasis' }}
                  />
                )}
                <RowContent
                  label={t('pages.billing.label.billing_email')}
                  qLabel={t('pages.billing.message.billing_email')}
                  width="240px"
                  vWidth="400px"
                  value={
                    billingEmail.emails.length > 0
                      ? billingEmail.emails
                      : [
                          t(
                            'pages.billing.description.billing_email_use_general',
                          ),
                        ]
                  }
                  valueStyles={{ color: 'Text.midEmphasis' }}
                />
              </React.Fragment>
            ))
          )}
        </Flex>
      </Flex>
    </SideMenuLayoutContentLayout>
  )
}
